export interface OverlayCanvas {
  readonly ctx: CanvasRenderingContext2D;
  readonly width: number;
  readonly height: number;
  setStackOrder(stackOrder: number): void;
  sync(): void;
  clear(): void;
  destroy(): void;
}

export type OverlayPlacement = 'back' | 'front';

/**
 * cytoscape's own canvas wrapper is a z-index 0 stacking context, so a layer that
 * has to stay behind the graph must be the container's first child, not just have
 * a lower z-index.
 */
export function createOverlayCanvas(
  container: HTMLElement,
  stackOrder: number,
  placement: OverlayPlacement = 'front'
): OverlayCanvas | null {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  canvas.style.position = 'absolute';
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = String(stackOrder);
  if (placement === 'back') {
    container.insertBefore(canvas, container.firstChild);
  } else {
    container.appendChild(canvas);
  }

  let width = 0;
  let height = 0;
  let ratio = 0;

  const overlay: OverlayCanvas = {
    ctx,
    get width() {
      return width;
    },
    get height() {
      return height;
    },
    setStackOrder(next: number) {
      canvas.style.zIndex = String(next);
    },
    sync() {
      const nextWidth = container.clientWidth;
      const nextHeight = container.clientHeight;
      const nextRatio = window.devicePixelRatio || 1;

      if (nextWidth === width && nextHeight === height && nextRatio === ratio) {
        return;
      }

      width = nextWidth;
      height = nextHeight;
      ratio = nextRatio;

      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    },
    clear() {
      ctx.clearRect(0, 0, width, height);
    },
    destroy() {
      canvas.remove();
    },
  };

  overlay.sync();

  return overlay;
}
